import { Link, useParams } from 'react-router-dom'
import { GrantForm, LiveLimitSelect, PlanSelect, SuspendControl } from '../../components/admin/WorkspaceControls'
import { TaskUsageTable } from '../../components/admin/UsageTables'
import { LedgerTable } from '../../components/LedgerTable'
import { JobStatusBadge } from '../../components/StatusBadges'
import { Badge, Card, ErrorNote, LoadingBlock, Stat } from '../../components/ui'
import { useAdminWorkspace } from '../../hooks/useAdmin'
import { elapsed, formatBytes, formatCredits, shortId, timeAgo } from '../../utils/format'

/** One workspace in full: plan and credits, people, shows, usage, ledger and jobs. */
export function AdminWorkspacePage() {
  const { workspaceId = '' } = useParams()
  const detail = useAdminWorkspace(workspaceId)
  if (detail.isPending) return <LoadingBlock />
  if (detail.isError) return <ErrorNote error={detail.error} />
  const { workspace, balance, subscription, members, shows, usage, ledger, jobs } = detail.data
  return (
    <div className="stack-lg">
      <Card
        title={
          <span className="row">
            {workspace.name} <Badge tone={workspace.plan === 'premium' ? 'accent' : 'neutral'}>{workspace.plan === 'premium' ? 'Premium' : 'Free'}</Badge>
            {workspace.status === 'suspended' && <Badge tone="danger">Stopped</Badge>}
          </span>
        }
        actions={
          <span className="row">
            <Link to={`/studio/admin/audit?workspace=${workspace.id}`}>Audit log</Link>
            <Link to="/studio/admin/billing">All workspaces</Link>
          </span>
        }
      >
        <div className="stack">
          <p className="muted small">
            {workspace.slug} · created {timeAgo(workspace.created_at)}
            {workspace.owner_email && ` · owner ${workspace.owner_email}`}
            {subscription && ` · Stripe subscription ${subscription.status}${subscription.current_period_end ? `, period ends ${new Date(subscription.current_period_end).toLocaleDateString()}` : ''}`}
          </p>
          <div className="grid-4">
            <Stat label="Available" value={formatCredits(balance.available)} note="credits" />
            <Stat label="Monthly left" value={formatCredits(balance.allowance)} />
            <Stat label="Extra" value={formatCredits(balance.extra)} />
            <Stat label="Held" value={formatCredits(balance.held, 'up')} note="for work in progress" />
          </div>
          <p className="muted small">Keeping {formatBytes(workspace.stored_bytes)} of recordings and generated files.</p>
          {workspace.written_off > 0 && (
            <p className="muted small">
              {formatCredits(workspace.written_off, 'up')} credits of work could not be charged to this workspace. Nobody was billed for it.
            </p>
          )}
          <div className="stack-sm">
            <div className="row">
              <span className="field-label control-label">Plan</span>
              <PlanSelect workspace={workspace} />
            </div>
            <div className="row">
              <span className="field-label control-label">Grant credits</span>
              <GrantForm workspaceId={workspace.id} />
            </div>
            <div className="row">
              <span className="field-label control-label">Status</span>
              <SuspendControl workspace={workspace} />
            </div>
            <div className="row">
              <span className="field-label control-label">Live</span>
              <LiveLimitSelect workspace={workspace} />
            </div>
          </div>
        </div>
      </Card>
      <div className="grid-2">
        <Card title="Members">
          <div className="table-wrap">
            <table className="table">
              <tbody>
                {members.map((m) => (
                  <tr key={m.user_id}>
                    <td>
                      {m.email}
                      {m.name && <div className="muted small">{m.name}</div>}
                    </td>
                    <td>{m.role}</td>
                    <td className="muted">{timeAgo(m.joined_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        <Card title="Shows">
          {shows.length === 0 ? (
            <p className="muted">No shows yet.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <tbody>
                  {shows.map((s) => (
                    <tr key={s.id}>
                      <td>{s.title}</td>
                      <td className="muted">{s.medium}</td>
                      <td className="num">
                        {s.episodes} {s.episodes === 1 ? 'episode' : 'episodes'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
      <Card title="Usage, last 30 days" actions={<Link to={`/studio/admin/usage?workspace=${workspace.id}`}>Charts and more</Link>}>
        <TaskUsageTable tasks={usage} />
      </Card>
      <div className="grid-2">
        <Card title="Ledger">{ledger.length === 0 ? <p className="muted">Nothing yet.</p> : <LedgerTable entries={ledger} />}</Card>
        <Card title="Recent jobs" actions={<Link to={`/studio/admin/jobs?workspace=${workspace.id}`}>All of its jobs</Link>}>
          {jobs.length === 0 ? (
            <p className="muted">No jobs yet.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <tbody>
                  {jobs.map((job) => (
                    <tr key={job.id}>
                      <td>
                        <JobStatusBadge status={job.status} />
                      </td>
                      <td>
                        <code>{job.job_type}</code> <code className="muted">{shortId(job.id)}</code>
                        {job.error && <div className="small error-text truncate" title={job.error}>{job.error}</div>}
                      </td>
                      <td className="muted">{timeAgo(job.created_at)}</td>
                      <td className="muted">{job.started_at ? elapsed(job.started_at, job.completed_at) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}
